import { CRUDInputElementConstants } from "@kuika/kuika-cl-model";

/**
 * KOA-17362 §7.6 Eksen 1 — which INPUT WIDGET (`ICRUDFormField.elementType`) a form
 * field may render with, given its data type.
 *
 * This mapping used to live only inside the human form wizard
 * (`common/form-wizard/form-wizard-custom.tsx#getElementTypeOptions`, a private method
 * reading component state), so nothing else could reuse it. The DSL now configures the
 * same knob on a workflow task screen (`SCREEN_FIELD … element:<Type>`) and must
 * validate against the SAME option set — two copies of this table would drift
 * immediately. Extracted verbatim as a pure function; the wizard delegates to it, so
 * the picker's contents and the DSL's validation can never disagree.
 *
 * Pure: the only piece of wizard state it needed was the list of TABLE type names
 * (a field typed as a table renders as a lookup Select), which is passed in.
 */
export interface IElementTypeOption {
  name: string;
  label: string;
  /** Web-only widgets are filtered out of the picker for Mobile projects. */
  isWebOnly: boolean;
}

/** Types that render a dedicated widget and never offer a Select/MultiSelect lookup. */
const TYPES_WITHOUT_SELECT: readonly string[] = ["boolean", "date", "datetime", "datetimeoffset", "image", "signature"];

/**
 * Canonical widget-family key for a field's data type.
 *
 * The SAME physical type reaches a form field under two spellings, from two
 * producers: a DB column carries `DatetimeOffset` (`KuikaDataType`, e.g.
 * `OracleConnector`/table schemas) while a WORKFLOW business-object parameter carries
 * `DateTimeOffset` (`FormMetadataService.ConvertWfDataTypeToKuikaDataType` ←
 * `EDataType.DateTimeOffset`), and the workflow type set also includes `Single`
 * (`workflow-param-types.ts`). The wizard's original switch was written for the
 * table-form spellings only, so a WORKFLOW date/Single parameter fell through to
 * `default` and its picker offered ONLY SelectBox/MultiSelect — no date picker, no
 * numeric input, even though the renderer supports them. Folding the type here fixes
 * that gap for the wizard and the DSL at once, from one table.
 */
function foldFieldTypeName(typeName: string): string {
  return (typeName || "").trim().toLowerCase();
}

/**
 * Widget options for a field of `typeName`, in the wizard's own order (the FIRST entry
 * is the default the wizard assigns). `tableTypeNames` are the project's
 * `schema.table` names — a field typed as one of them is a lookup and offers only the
 * Select variants.
 */
export function getFormElementTypeOptions(
  typeName: string,
  tableTypeNames: readonly string[] = []
): IElementTypeOption[] {
  if (!typeName || typeName === "") return [];
  const folded: string = foldFieldTypeName(typeName);

  let selectOptions: IElementTypeOption[] = [];
  if (!TYPES_WITHOUT_SELECT.includes(folded)) {
    selectOptions = [
      { name: CRUDInputElementConstants.Select, label: "SelectBox", isWebOnly: false },
      { name: CRUDInputElementConstants.MultiSelect, label: "MultiSelect", isWebOnly: true }
    ];
  }

  if (tableTypeNames.includes(typeName)) return selectOptions;

  let options: IElementTypeOption[];
  switch (folded) {
    case "boolean":
      options = [
        { name: CRUDInputElementConstants.Checkbox, label: "Checkbox", isWebOnly: false },
        { name: CRUDInputElementConstants.ToggleSwitch, label: "Switch", isWebOnly: false }
      ];
      break;

    case "date":
      options = [
        { name: CRUDInputElementConstants.PickerDate, label: "Date", isWebOnly: false },
        { name: CRUDInputElementConstants.PickerDateTime, label: "DateTime", isWebOnly: false }
      ];
      break;

    case "datetime":
    case "datetimeoffset":
      options = [
        { name: CRUDInputElementConstants.PickerDateTime, label: "DateTime", isWebOnly: false },
        { name: CRUDInputElementConstants.PickerDate, label: "Date", isWebOnly: false }
      ];
      break;

    case "decimal":
    case "double":
    case "single":
    case "int16":
    case "int32":
    case "int64":
      options = [
        { name: CRUDInputElementConstants.InputNumeric, label: "NumberInput", isWebOnly: false },
        { name: CRUDInputElementConstants.Currency, label: "Currency", isWebOnly: true },
        { name: CRUDInputElementConstants.Percent, label: "Percent", isWebOnly: true }
      ];
      break;

    case "string":
      options = [
        { name: CRUDInputElementConstants.TextInput, label: "TextInput", isWebOnly: false },
        { name: CRUDInputElementConstants.TextArea, label: "TextArea", isWebOnly: false },
        { name: CRUDInputElementConstants.InputNumeric, label: "NumberInput", isWebOnly: false },
        { name: CRUDInputElementConstants.Password, label: "Password", isWebOnly: false },
        { name: CRUDInputElementConstants.Email, label: "Email", isWebOnly: false },
        { name: CRUDInputElementConstants.Url, label: "Url", isWebOnly: false }
      ];
      break;

    case "guid":
      options = [
        { name: CRUDInputElementConstants.TextInput, label: "TextInput", isWebOnly: false },
        { name: CRUDInputElementConstants.TextArea, label: "TextArea", isWebOnly: false }
      ];
      break;

    case "image":
      options = [{ name: CRUDInputElementConstants.ImageView, label: "Image", isWebOnly: false }];
      break;

    case "signature":
      options = [{ name: CRUDInputElementConstants.Signature, label: "Signature", isWebOnly: false }];
      break;

    default:
      options = [];
      break;
  }

  options.push(...selectOptions);
  return options;
}

/** Every canonical widget constant, plus the wizard's UI labels as accepted aliases. */
const ELEMENT_TYPE_ALIASES: readonly (readonly [string, string])[] = [
  [CRUDInputElementConstants.Checkbox, CRUDInputElementConstants.Checkbox],
  [CRUDInputElementConstants.ToggleSwitch, CRUDInputElementConstants.ToggleSwitch],
  ["Switch", CRUDInputElementConstants.ToggleSwitch],
  [CRUDInputElementConstants.PickerDate, CRUDInputElementConstants.PickerDate],
  ["Date", CRUDInputElementConstants.PickerDate],
  [CRUDInputElementConstants.PickerDateTime, CRUDInputElementConstants.PickerDateTime],
  ["DateTime", CRUDInputElementConstants.PickerDateTime],
  [CRUDInputElementConstants.InputNumeric, CRUDInputElementConstants.InputNumeric],
  ["NumberInput", CRUDInputElementConstants.InputNumeric],
  ["Number", CRUDInputElementConstants.InputNumeric],
  [CRUDInputElementConstants.Currency, CRUDInputElementConstants.Currency],
  [CRUDInputElementConstants.Percent, CRUDInputElementConstants.Percent],
  [CRUDInputElementConstants.TextInput, CRUDInputElementConstants.TextInput],
  [CRUDInputElementConstants.TextArea, CRUDInputElementConstants.TextArea],
  [CRUDInputElementConstants.Password, CRUDInputElementConstants.Password],
  [CRUDInputElementConstants.Email, CRUDInputElementConstants.Email],
  [CRUDInputElementConstants.Url, CRUDInputElementConstants.Url],
  [CRUDInputElementConstants.Select, CRUDInputElementConstants.Select],
  ["SelectBox", CRUDInputElementConstants.Select],
  [CRUDInputElementConstants.MultiSelect, CRUDInputElementConstants.MultiSelect],
  ["MultiSelect", CRUDInputElementConstants.MultiSelect],
  [CRUDInputElementConstants.ImageView, CRUDInputElementConstants.ImageView],
  ["Image", CRUDInputElementConstants.ImageView],
  [CRUDInputElementConstants.Signature, CRUDInputElementConstants.Signature]
];

/**
 * Fold a widget spelling for lookup: drop spaces, hyphens and underscores, lowercase.
 * `toLowerCase` is locale-independent in JS (unlike `toLocaleLowerCase`), and the
 * widget names are pure ASCII, so this is culture-invariant.
 */
function foldElementTypeName(value: string): string {
  return (value || "").replace(/[\s\-_]/g, "").toLowerCase();
}

const ELEMENT_TYPE_BY_FOLD: Map<string, string> = new Map<string, string>(
  ELEMENT_TYPE_ALIASES.map(([alias, canonical]: readonly [string, string]): [string, string] => [
    foldElementTypeName(alias),
    canonical
  ])
);

/**
 * Resolve an author-written widget name to the canonical `CRUDInputElementConstants`
 * value the metadata must carry, or undefined when it is not a widget at all.
 * Accepts the canonical spelling ("Picker Date Time"), a space-free/cased variant
 * ("pickerdatetime"), hyphen/underscore forms, and the wizard's UI labels ("Switch",
 * "NumberInput", "SelectBox") — an author should not have to know which of the two
 * vocabularies the model stores.
 */
export function resolveElementTypeName(value: string): string | undefined {
  const folded: string = foldElementTypeName(value);
  if (folded.length === 0) return undefined;
  return ELEMENT_TYPE_BY_FOLD.get(folded);
}

/**
 * True when `elementType` is one of the widgets the field's data type offers. An
 * UNKNOWN field type (e.g. a table/lookup type this caller cannot enumerate) is
 * permissive: refusing there would block a legitimate lookup Select.
 */
export function isElementTypeValidForFieldType(elementType: string, typeName: string): boolean {
  const options: IElementTypeOption[] = getFormElementTypeOptions(typeName);
  if (options.length === 0) return true;
  return options.some((option: IElementTypeOption): boolean => option.name === elementType);
}

/** Human-readable list of the widgets a field type offers — for a fixable error. */
export function describeElementTypeOptions(typeName: string): string {
  return getFormElementTypeOptions(typeName)
    .map((option: IElementTypeOption): string => option.name)
    .join(", ");
}
